'use client';

import { Logo } from '@ahamove/icons';
import { PaymentMethodCode, type PaymentMethod } from '@ahm/api-wrappers-core';
import { useAuth } from '@ahm/react-auth';
import {
  ActionIcon,
  Avatar,
  Button,
  Center,
  Group,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { closeAllModals, type ContextModalProps } from '@mantine/modals';
import { X as IconX } from 'lucide-react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import IconLinkPrimary from '@/assets/icons/link-primary-icon.svg';
import {
  usePostBindingWalletMutation,
  type BindingWalletRequestParams,
  type BindingWalletRes,
} from '@/features/booking/api/payment';
import { objectToQuery } from '@/lib/utils';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type LinkWalletModalProps = {
  method: PaymentMethod;
};

const LinkWalletModal = ({
  innerProps,
}: ContextModalProps<LinkWalletModalProps>) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { method } = innerProps;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const localSuffix = useLocaleSuffix();
  const methodName = method[`name_${localSuffix}`];
  const { session } = useAuth();
  const user = session?.user;

  // const { setDetectModalId, clearDetectModalId } = useDetectModal();
  const tPayment = useTranslations('payment');
  const { setPaymentMethod } = useBookingStore((state) => state);
  const { mutate: BindingEWallet } = usePostBindingWalletMutation();

  const handleLinkLater = () => {
    setPaymentMethod(method);
    closeAllModals();
  };

  const handleLinkWallet = () => {
    const queryParams = {
      paymentAction: 'link',
      promo_code: searchParams.get('promo_code') ?? undefined,
      methodCode: method.code,
    };

    const returnUrl = `${process.env.NEXT_PUBLIC_APP_URI}${localSuffix === 'en_us' ? '/en' : ''}${pathname}?${objectToQuery(
      queryParams
    )}`;

    const params: BindingWalletRequestParams = {
      payment_provider: method.code,
      return_url: returnUrl,
      c_id: user?._id,
    };
    try {
      BindingEWallet(params, {
        onSuccess: (result: BindingWalletRes) => {
          if (returnUrl) {
            window.history.pushState({}, '', returnUrl);
            if (
              method.code === PaymentMethodCode.Zalopay.toLowerCase() &&
              'binding_qr_link' in result
            ) {
              router.push(result.binding_qr_link);
            }
            if (
              method.code === PaymentMethodCode.Shopeepay.toLowerCase() &&
              'redirect_url_web' in result
            ) {
              router.push(result.redirect_url_web);
            }
            if (
              method.code === PaymentMethodCode.Momo.toLowerCase() &&
              'qr_code_url' in result
            ) {
              router.push(result.qr_code_url as string);
            }
          }
        },
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <Fragment style={{ position: 'relative' }}>
      <ActionIcon
        size={44}
        variant="transparent"
        c="neutral"
        pos="absolute"
        top={0}
        left={isMobile ? 0 : 'calc(100% - 44px)'}
        onClick={handleLinkLater}
        style={{ outline: 'none !important' }}
      >
        <IconX />
      </ActionIcon>
      <Center pt={48}>
        <Group mb={24} gap={24}>
          <Logo
            width={64}
            height={64}
            style={{ background: '#0A3263', borderRadius: 8 }}
          />

          <IconLinkPrimary color="primary" size="26px" />
          <Avatar w={64} h={64} radius={4} src={method.icon_url} />
        </Group>
      </Center>
      <Stack gap={8} justify="space-around">
        <Title ta="center" order={3}>
          {tPayment('e_wallet.link_ewallet_for_faster_payment', {
            wallet: methodName,
          })}
        </Title>
        <Text ta="center" size="md">
          {tPayment('e_wallet.link_ewallet_for_faster_payment_desc', {
            wallet: methodName,
          })}
        </Text>
      </Stack>
      <Stack mt={12} gap={8}>
        <Button
          size="md"
          h={rem(48)}
          fw={700}
          fullWidth
          styles={{
            label: {
              fontSize: rem(14),
            },
          }}
          onClick={handleLinkWallet}
        >
          {tPayment('link_now')}
        </Button>

        <Button
          variant="outline"
          size="md"
          h={rem(48)}
          fw={700}
          fullWidth
          color="neutral.3"
          styles={{
            label: {
              color: 'var(--mantine-color-neutral-7)',
              fontSize: rem(14),
            },
          }}
          onClick={handleLinkLater}
        >
          {tPayment('later')}
        </Button>
      </Stack>
    </Fragment>
  );
};

export default LinkWalletModal;
